import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';

import fetchVehicleByRego from '@/utils/fetch-vehicle-by-rego';
import { getVehicleDetailsHrefFromNames } from '@/utils/get-vehicle-details-href';
import { logTrackingEvent } from '@/utils/tracking';

const useLookupNumberPlate = () => {
  const router = useRouter();
  const [numberPlateNotFound, setNumberPlateNotFound] = useState(false);
  const [submittingNumberPlate, setSubmittingNumberPlate] = useState(false);
  const [regoError, setRegoError] = useState<string | null>(null);

  const clearCurrentTimer = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
  }, []);

  const clearRegoSearchError = useCallback(() => {
    setRegoError(null);
  }, []);

  const isMounted = useRef(true);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      clearCurrentTimer();
    };
  }, []);

  const onSubmitNumberPlate = async (numberPlate: string) => {
    if (numberPlate) {
      try {
        logTrackingEvent({
          event: 'searchRego',
          numberPlate,
        });

        setSubmittingNumberPlate(true);

        const regoLookupResponse = await fetchVehicleByRego(numberPlate);

        if (regoLookupResponse.data.error) {
          setSubmittingNumberPlate(false);
          setRegoError(regoLookupResponse.data.error);
        }

        const { make, model, year, variant, error } = regoLookupResponse.data;

        if (!error && make && model) {
          setNumberPlateNotFound(false);
          setRegoError(null);
          router.push(getVehicleDetailsHrefFromNames({ make, model, year, variant }));
        } else {
          setNumberPlateNotFound(true);
          setRegoError('Error submitting number plate');
        }
      } catch (e) {
        console.error('Error submitting number plate', e);
        setSubmittingNumberPlate(false);

        if (isMounted.current) {
          setNumberPlateNotFound(true);
          setRegoError('Error submitting number plate');
        }
      }
    }
  };

  const debouncedSubmit = (numberPlate: string, delay = 300) => {
    clearCurrentTimer();

    debounceTimer.current = setTimeout(() => {
      onSubmitNumberPlate(numberPlate);
    }, delay);
  };

  const immediateSubmit = (numberPlate: string) => {
    clearCurrentTimer();
    onSubmitNumberPlate(numberPlate);
  };

  const cancelSearch = () => {
    clearCurrentTimer();
    setSubmittingNumberPlate(false);
    setNumberPlateNotFound(false);
  };

  return {
    numberPlateNotFound,
    onSubmitNumberPlate,
    debouncedSubmit,
    immediateSubmit,
    cancelSearch,
    setNumberPlateNotFound,
    submittingNumberPlate,
    regoError,
    clearRegoSearchError,
  };
};

export { useLookupNumberPlate };
